import type { HomeReview, ReviewCategory, ReviewItem } from '../types';

export type ReviewSearchResult = ReviewItem & {
  categoryName?: string;
};

export const hasHydratedReviewSearchInventory = (
  review?: HomeReview | null
): boolean => {
  if (!review) {
    return false;
  }

  const reviewData = (review as any).reviewData;
  return [
    review.template?.categories,
    review.categories,
    reviewData?.template?.categories,
  ].some(categories => Array.isArray(categories));
};

export const toNavigableReviewItemId = (
  id: string | number
): number | null => {
  const numericId = Number(id);
  return Number.isFinite(numericId) ? numericId : null;
};

const normalize = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLocaleLowerCase()
    .replace(/\s+/g, ' ');

const itemKey = (item: ReviewItem): string => {
  if (item.id !== undefined && item.id !== null) {
    return `id:${String(item.id)}`;
  }

  if (item.tempId) {
    return `temp:${item.tempId}`;
  }

  return `name:${normalize(item.name)}:${String(item.categoryId ?? '')}`;
};

/**
 * Build the searchable item inventory from the review snapshot already loaded
 * for the current screen. Historical response items and review-scoped custom
 * items are included because they are not guaranteed to exist in today's
 * template tree.
 */
export const collectReviewSearchItems = (
  review?: HomeReview | null,
  additionalItems: ReviewItem[] = []
): ReviewSearchResult[] => {
  if (!review) {
    return [];
  }

  const results = new Map<string, ReviewSearchResult>();
  const categoryNames = new Map<string, string>();

  const addItem = (item: ReviewItem | null | undefined, categoryName?: string) => {
    if (!item?.name) {
      return;
    }

    const resolvedCategoryName =
      item.categoryName ||
      item.category?.name ||
      categoryName ||
      (item.categoryId !== undefined && item.categoryId !== null
        ? categoryNames.get(String(item.categoryId))
        : undefined);
    const key = itemKey(item);
    const existing = results.get(key);

    results.set(key, {
      ...existing,
      ...item,
      categoryName: resolvedCategoryName || existing?.categoryName,
    });
  };

  const visitCategories = (categories: ReviewCategory[] | undefined) => {
    for (const category of categories || []) {
      if (category.id !== undefined && category.id !== null && category.name) {
        categoryNames.set(String(category.id), category.name);
      }

      for (const item of category.items || []) {
        addItem(item, category.name);
      }

      visitCategories(category.subcategories);
    }
  };

  visitCategories(review.template?.categories);
  visitCategories((review as any).reviewData?.template?.categories);
  visitCategories(review.categories);

  for (const item of review.onDemandItems || []) {
    addItem(item);
  }
  for (const item of (review as any).reviewData?.onDemandItems || []) {
    addItem(item);
  }
  for (const item of additionalItems) {
    addItem(item);
  }

  const responses = [
    ...(review.responses || []),
    ...((review as any).reviewData?.responses || []),
  ];
  for (const response of responses) {
    addItem(response?.item || response?.reviewItem);
  }

  return Array.from(results.values());
};

export const searchReviewItemsLocally = (
  review: HomeReview | null | undefined,
  query: string,
  additionalItems: ReviewItem[] = []
): ReviewSearchResult[] => {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) {
    return [];
  }

  const terms = normalizedQuery.split(' ');

  return collectReviewSearchItems(review, additionalItems)
    .filter(item => {
      const searchableText = normalize([
        item.name,
        item.description,
        item.categoryName,
      ].filter(Boolean).join(' '));

      return terms.every(term => searchableText.includes(term));
    })
    .sort((left, right) => {
      const leftName = normalize(left.name);
      const rightName = normalize(right.name);
      const leftStartsWith = leftName.startsWith(normalizedQuery) ? 0 : 1;
      const rightStartsWith = rightName.startsWith(normalizedQuery) ? 0 : 1;

      return leftStartsWith - rightStartsWith || leftName.localeCompare(rightName);
    });
};
